import logger from './logger';
import { useState, useEffect, useRef } from "react";
import Cookies from 'js-cookie';
import { resolveBookingDateTime } from './resolveBookingDateTime';
import { clearSessionStorage, removeSessionStorageValue, STORAGE_KEYS } from "./SessionStorage";


/**
 * Tracks whether the page header should render in its compact "stuck" state.
 *
 * Uses a hysteresis band instead of a single threshold so the state cannot
 * oscillate when the sentinel hovers at the viewport's top edge during slow
 * scrolling (which previously caused the sticky header to flicker). The header
 * becomes sticky only after the sentinel is scrolled `enterOffset`px past the
 * top, and releases only once it comes back within `exitOffset`px of the top.
 * Reads are throttled to one per animation frame.
 */
export const useSticky = ({ enterOffset = 56, exitOffset = 8, headerRef, scrollContainerRef } = {}) => {
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef(null);
  const stickyRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let rafId = null;
    let ticking = false;
    const scrollContainer = scrollContainerRef?.current;

    const evaluate = () => {
      ticking = false;
      const el = sentinelRef.current;
      if (!el) return;

      // Verification pages scroll inside a viewport-sized frame on mobile.
      const hasScrollContainer = scrollContainer &&
        /^(auto|scroll)$/.test(window.getComputedStyle(scrollContainer).overflowY);
      const top = el.getBoundingClientRect().top -
        (hasScrollContainer ? scrollContainer.getBoundingClientRect().top : 0);
      if (!stickyRef.current && top <= -enterOffset) {
        const header = headerRef?.current;
        if (header) {
          const titleHeight = header.querySelector('.title-container')?.getBoundingClientRect().height || 0;
          const guestHeader = header.querySelector('.select-services-header');
          const guestStyle = guestHeader ? window.getComputedStyle(guestHeader) : null;
          const collapsingHeight = titleHeight +
            (parseFloat(guestStyle?.paddingTop) || 0) + (parseFloat(guestStyle?.paddingBottom) || 0);
          const root = hasScrollContainer ? scrollContainer : document.scrollingElement || document.documentElement;
          const viewportHeight = hasScrollContainer ? root.clientHeight : window.innerHeight;
          const remainingScrollRange = root.scrollHeight - viewportHeight - collapsingHeight;
          if (remainingScrollRange < enterOffset) return;
        }
        stickyRef.current = true;
        setIsSticky(true);
      } else if (stickyRef.current && top >= -exitOffset) {
        stickyRef.current = false;
        setIsSticky(false);
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      rafId = window.requestAnimationFrame(evaluate);
    };

    // Initial check covers reloads / restored scroll positions.
    evaluate();

    window.addEventListener("scroll", onScroll, { passive: true });
    scrollContainer?.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      scrollContainer?.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [enterOffset, exitOffset, headerRef, scrollContainerRef]);

  return { isSticky, sentinelRef };
};


export const generateTimeSlots = (startTime, endTime, interval) => {
    const timeSlots = [];

    const parseTime = (timeString) => {
      if (!timeString || typeof timeString !== "string" || !timeString.includes(":")) {
        logger.error("Invalid time string:", timeString);
        return new Date(); // or handle differently
      }

      const [hour, minute] = timeString.split(":").map(Number);
      const date = new Date();
      date.setHours(hour, minute, 0, 0); // Set hours and minutes
      return date;
    };

  
    let current = parseTime(startTime);
    const end = parseTime(endTime);
  
    while (current <= end) {
      timeSlots.push(new Date(current)); // Store as Date object
      current.setMinutes(current.getMinutes() + interval);
    }
  
    return timeSlots;
  };





  

  export const getWeekdays = () => {
    const weekdays = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() + i);
        
        const day = date.toLocaleDateString('en-US', { weekday: 'short' });
        const newDate = `${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'short' })}`;

        weekdays.push({ day, newDate });
    }
    return weekdays;
};

// Helper function to convert short day name to full day name
export const getFullDayName = (shortDay) => {
    const daysMap = {
      sun: "sunday",
      mon: "monday",
      tue: "tuesday",
      wed: "wednesday",
      thu: "thursday",
      fri: "friday",
      sat: "saturday",
    };
    return daysMap[shortDay.toLowerCase()] || shortDay; // Return the full day name
  };

  export const getShortDayName = (fullDay) => {
    const daysMap = {
      sunday    : "sun",
      monday    : "mon",
      tuesday   : "tue",
      wednesday : "wed",
      thursday  : "thu",
      friday    : "fri",
      saturday  : "sat",
    };
    return daysMap[fullDay.toLowerCase()] || fullDay; // Return the full day name
  };


// GET END TIME FROM START TIME AND DURATION 
export function calculateEndTime(startTime, durationInMinutes) {
  const [hours, minutes, seconds] = startTime.split(":").map(Number);
  let totalMinutes = hours * 60 + minutes + durationInMinutes;
  let endHours = Math.floor(totalMinutes / 60) % 24;
  let endMinutes = totalMinutes % 60;
  //return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
}

  // Get the maximum total duration among all members for group appointments
  export function getMaxTotalDuration(members) {
    if (!members || !Array.isArray(members)) return 0;

    // Calculate total duration for each member
    const memberDurations = members.map(member => {
      if (!member.services || !Array.isArray(member.services)) return 0;
      return member.services.reduce((sum, service) => sum + (parseInt(service.duration) || 0), 0);
    });

    // Return the maximum duration
    return Math.max(...memberDurations, 0);
  }

  
  // Function to subtract (TOTAL SERVICE TIME) minutes from (OUTLET CLOSING TIME)TIMESLOTS
  export function subtractMinutes(time, minutes) {
    let [hours, mins] = time.split(":").map(Number);
    let totalMinutes = hours * 60 + mins - minutes;
    if (totalMinutes < 0) totalMinutes = 0;
    let newHours = Math.floor(totalMinutes / 60);
    let newMinutes = totalMinutes % 60;
    return `${String(newHours).padStart(2, "0")}:${String(newMinutes).padStart(2, "0")}:00`;
  }

  // Adds minutes to a time string in "HH:mm" format
  export const addMinutesToHHMM = (time, minutesToAdd) => {
    const [hours, minutes] = time.split(":").map(Number);
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes + minutesToAdd);
    const newHours = date.getHours().toString().padStart(2, '0');
    const newMinutes = date.getMinutes().toString().padStart(2, '0');
    return `${newHours}:${newMinutes}`;
  };

  // Generate a new slot object
  export const getNextSlot = (lastSlot) => {
    const newStart = addMinutesToHHMM(lastSlot.start, 15);
    return {
      start: newStart,
      end: newStart,
    };
  };

// CALCULATE GST AMOUNT FROM A SUBTOTAL
export const calculateGST = (subTotal, gstRate = 9) => {
  const parsedSubTotal = Number(subTotal) || 0;
  const parsedRate = Number(gstRate) || 0;
  return ((parsedSubTotal * parsedRate) / 100).toFixed(2);
};


// SPLIT DATE TIME INTO DATE AND TIME
export const splitDateTime = (dateTime) => {
  if (!dateTime) {
    return {
      date: "",
      time: "",
    };
  }

  const [date, ...timeParts] = dateTime.trim().split(" ");

  return {
    date,
    time: timeParts.join(" "),
  };
};




export const formatDate = (dateString) => {
  // console.log("dateString", dateString);

  const date = new Date();
  const today = date.toLocaleDateString("en-GB").replace(/\//g, "-");
  // console.log("today date", today);
  
  if (!dateString) {
    dateString = today;
  }

  const [day, month, year] = dateString.split("-");
  const formattedDate = new Date(Number(year), Number(month) - 1, Number(day));
  const dayName = formattedDate.toLocaleDateString("en-US", { weekday: "short" });
  const dayNum = formattedDate.getDate();
  const monthName = formattedDate.toLocaleDateString("en-US", { month: "short" });
  const yearNum = formattedDate.toLocaleDateString("en-US", { year: "numeric" });
  return { dayName, dayNum, monthName, yearNum };
};


// [DD-MM-YYYY] format to ISO-friendly [YYYY-MM-DD]
export function formatDateToISO(dateStr) {
  const [day, month, year] = dateStr.split("-");
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}




// CONVERT TIME INTO 24 FORMAT
export const convertTo24HourFormat = (time12hr) => {
  if (!time12hr) {
    // console.warn("Invalid time input: ", time12hr);
    return null; // or return a default value like "00:00"
  }
  const [time, modifier] = time12hr.split(" ");
  if (!time || !modifier) {
    logger.warn("Incorrect time format:", time12hr);
    return null;
  }
  let [hours, minutes] = time.split(":");
  if (modifier === "PM" && hours !== "12") {
    hours = parseInt(hours, 10) + 12;
  } else if (modifier === "AM" && hours === "12") {
    hours = "00";
  }
  return `${hours}:${minutes}`;
};


// CONVERT 24-HOUR TIME (e.g. "18:00:00") TO SLOT LABEL USED BY SLOTS APIs (e.g. "06:00PM")
export const formatToSlotLabel = (time) => {
  return resolveBookingDateTime(null, time).slotLabel;
};

// CONVERT TIME INTO AM/PM FORMAT
export const formatTimeToAMPM = (time) => {
  if (!time || typeof time !== "string") return "";

  // Already in 12-hour format
  if (/am|pm/i.test(time)) {
    return time
      .replace(/(\d{1,2}:\d{2}):\d{2}/, '$1')
      .replace(/am|pm/i, (match) => match.toUpperCase());
  }

  const [hours, minutes, seconds = "00"] = time.split(":");

  const date = new Date();
  date.setHours(parseInt(hours, 10));
  date.setMinutes(parseInt(minutes, 10));
  date.setSeconds(parseInt(seconds, 10));

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// ADD SECONDS FROM TIME
export const formatToSeconds = (time) => time.length === 5 ? `${time}:00` : time;

// REMOVE SECONDS FROM TIME
export const removeSeconds = (time) => {
  return time?.split(':').slice(0, 2).join(':');
};


// REMOVE HTML TAGS 
export const removeHtmlTag = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};


// Create a date string with the desired time and +08:00 offset
export function getSingaporeDate(dateStr) {
  return new Date(`${dateStr}+08:00`);
}



export const goToHome = () => {
    clearSessionStorage();
    removeSessionStorageValue(STORAGE_KEYS.TIMES_UP);
    removeSessionStorageValue(STORAGE_KEYS.TIMER_ACTIVE);
    removeSessionStorageValue(STORAGE_KEYS.COUNTDOWN_END_TIME);
    Cookies.remove("membership");
    Cookies.remove("booking");
    window.location.replace("/");
}



// GET SINGAPORE CURRENT DATE 
export const getCurrentDateInSingapore = () => {
  const currentDateStr = new Date().toLocaleString("en-GB", {
    timeZone: "Asia/Singapore",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return currentDateStr.split(',')[0].replace(/\//g, '-');
};

// GET DATE AFTER N DAYS IN [DD-MM-YYYY] FORMAT
export const getDateAfterDays = (dateStr, daysToAdd = 13) => {
  const [day, month, year] = dateStr.split("-").map(Number);
  const dateObj = new Date(Date.UTC(year, month - 1, day));
  dateObj.setUTCDate(dateObj.getUTCDate() + daysToAdd);

  return `${String(dateObj.getUTCDate()).padStart(2, "0")}-${String(
    dateObj.getUTCMonth() + 1
  ).padStart(2, "0")}-${dateObj.getUTCFullYear()}`;
};

// BUILD PAXES PARAM FROM MEMBERS FOR SLOT / BOOKING API PAYLOADS
export const buildPaxesParam = (members = []) => {
  if (!Array.isArray(members)) return [];

  return members.map((member, index) => ({
    pax: index + 1,
    name: member?.name ?? "",
    services: (member?.services ?? []).map((service) => ({
      service_id: service.id,
      is_autoassigned: service.therapistData?.id ? 0 : 1,
      therapist_id: service.therapistData?.id ?? null,
    })),
  }));
};


// GET SINGAPORE CURRENT TIME
export const getCurrentSingaporeTime = () => {
    return new Date().toLocaleString("en-GB", {
      timeZone: "Asia/Singapore",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };



// GET 2 WEAKS DATES 
export const getNext14SingaporeDates = (startDateStr) => {
  const [day, month, year] = startDateStr.split('-');
  const startDate = new Date(`${year}-${month}-${day}T00:00:00+08:00`);

  const dateArray = [];

  for (let i = 0; i < 14; i++) {
    const tempDate = new Date(startDate);
    tempDate.setDate(startDate.getDate() + i);

    const formatted = tempDate.toLocaleString("en-GB", {
      timeZone: "Asia/Singapore",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).split(',')[0].replace(/\//g, '-');

    dateArray.push(formatted);
  }

  return dateArray;
};

// EXTRACT OPEN/CLOSE TIME
export const extractFullTiming = (selectedOutletTiming, selectedDate) => {
  const result = [];
  selectedOutletTiming.forEach((dayObj) => {
    // Only process the selected date
    if (dayObj[selectedDate]) {
      dayObj[selectedDate].forEach((t) => {
        result.push({
          open: t.open,
          close: t.close,
          off: t.off,
        });
      });
    }
  });
  return result;
}; 


// ADD MINUTES INTO TIMESLOTS 
export const addMinutesToTime = (timeStr, minutesToAdd) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes + minutesToAdd, 0, 0);

  const padded = (num) => String(num).padStart(2, '0');
  return `${padded(date.getHours())}:${padded(date.getMinutes())}`;
};
